import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

export const ProtectedRoute = ({ children, requiredRoles = [], requiredGroups = [], adminOnly = false }) => {
    const { isAuthenticated, loading, hasRole, hasAnyRole, hasGroup, hasAnyGroup, isAdmin, user } = useAuth();

    // console.log('ProtectedRoute check:', {
    //     isAuthenticated,
    //     loading,
    //     user,
    //     requiredRoles,
    //     userRoles: user?.roles,
    //     hasAnyRoleResult: requiredRoles.length > 0 ? hasAnyRole(requiredRoles) : true
    // });

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        console.log('User not authenticated, redirecting to login...');
        // Redirect to appropriate login page based on route
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/applicant/')) {
            return <Navigate to="/applicant/login" replace />;
        }
        return <Navigate to="/login" replace />;
    }

    // Check admin-only access
    if (adminOnly && !isAdmin()) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Access Denied</h4>
                    <p>You don't have sufficient permissions to access this page.</p>
                    <hr />
                    <p className="mb-0">Please contact your administrator if you believe this is an error.</p>
                </div>
            </div>
        );
    }    // Check required roles
    if (requiredRoles.length > 0) {
        // console.log('ProtectedRoute checking roles:', {
        //     requiredRoles,
        //     userRoles: user?.roles,
        //     checkResult: hasAnyRole(requiredRoles)
        // });
        
        if (!hasAnyRole(requiredRoles)) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                    <div className="alert alert-warning" role="alert">
                        <h4 className="alert-heading">Insufficient Permissions</h4>
                        <p>You need one of the following roles to access this page: {requiredRoles.join(', ')}</p>
                        <hr />
                        <p className="mb-0">Please contact your administrator to request appropriate permissions.</p>
                    </div>
                </div>
            );
        }
    }

    // Check required groups
    if (requiredGroups.length > 0 && !hasAnyGroup(requiredGroups)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="alert alert-warning" role="alert">
                    <h4 className="alert-heading">Group Access Required</h4>
                    <p>You need to be a member of one of the following groups: {requiredGroups.join(', ')}</p>
                    <hr />
                    <p className="mb-0">Please contact your administrator to be added to the appropriate group.</p>
                </div>
            </div>
        );
    }

    return children;
};

export const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();

    console.log('PublicRoute check:', {
        isAuthenticated,
        loading,
        user,
        userRoles: user?.roles,
        currentPath: window.location.pathname
    });

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }    if (isAuthenticated) {
        console.log('User is authenticated, determining redirect...');
        // Redirect authenticated users to their appropriate dashboard
        if (user?.roles?.includes('APPLICANT')) {
            console.log('Redirecting applicant to /applicant/dashboard');
            return <Navigate to="/applicant/dashboard" replace />;
        } else {
            console.log('Redirecting staff/admin to /');
            // Staff/Admin users go to main dashboard
            return <Navigate to="/" replace />;
        }
    }

    console.log('User not authenticated, showing public route content');
    return children;
};

// Role-specific route components for convenience
export const AdminRoute = ({ children }) => (
    <ProtectedRoute adminOnly={true}>
        {children}
    </ProtectedRoute>
);

export const StaffRoute = ({ children }) => (
    <ProtectedRoute requiredRoles={['ADMIN', 'STAFF']}>
        {children}
    </ProtectedRoute>
);

export const StudentRoute = ({ children }) => (
    <ProtectedRoute requiredRoles={['ADMIN', 'STAFF', 'STUDENT']}>
        {children}
    </ProtectedRoute>
);

export const ApplicantRoute = ({ children }) => {
    const { isAuthenticated, user, loading } = useAuth();
    
    // console.log('ApplicantRoute check:', {
    //     isAuthenticated,
    //     user,
    //     loading,
    //     userRoles: user?.roles
    // });
    
    return (
        <ProtectedRoute requiredRoles={['APPLICANT']}>
            {children}
        </ProtectedRoute>
    );
};
